import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getAppParams, getUrlParams } from "../apiService/timespam";
import "./Theme.css";
import "./SkinAssessment.css";
import { APIV4_BASE_URL } from "../apiService/apiClient";

const downloadPdf = (reportUrl) => {
  if (!reportUrl) return;
  const link = document.createElement("a");
  link.href = reportUrl;
  link.setAttribute("download", "report.pdf");
  link.style.display = "none";
  document.body.appendChild(link);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  if (isIOS) window.open(reportUrl, "_blank");
  else link.click();
  document.body.removeChild(link);
};

const SkinAssessment = () => {
  const { t } = useTranslation();
  const { theme, lang } = getUrlParams();
  const { goqiiUserId, nonce, apiKey, signature, goqiiAccessToken, appVersion, appType } = getAppParams();

  useEffect(() => {
    document.documentElement.className = theme === "dark" ? "dark" : "";
    if (lang === "ar") document.documentElement.dir = "rtl";
  }, [theme, lang]);

  const [view, setView] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [userDetails, setUserDetails] = useState({});
  const [currentResult, setCurrentResult] = useState(null);

  const buildQs = useCallback((extra = {}) =>
    new URLSearchParams({ goqiiUserId, nonce, signature, apiKey, goqiiAccessToken, appVersion, appType, ...extra }).toString(),
    [goqiiUserId, nonce, signature, apiKey, goqiiAccessToken, appVersion, appType]
  );

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const qs = buildQs();
        const [skinRes, profileRes] = await Promise.all([
          fetch(`${APIV4_BASE_URL}/supplementarykit/fetch_skin_ai_health_records?${qs}`),
          fetch(`${APIV4_BASE_URL}/ruser/fetch_profile?${qs}`),
        ]);
        const skinJson = await skinRes.json();
        const profileJson = await profileRes.json();

        if (skinJson.code === 200) {
          setResults(skinJson.data.records || []);
          setUserDetails(skinJson.data.userDetails || {});
        }
        if (profileJson.data) {
          setUserDetails((prev) => ({ ...prev, ...profileJson.data }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [buildQs]);

  if (loading) {
    return (
      <div className="sk-wrapper">
        {[1, 2, 3].map((i) => <div key={i} className="sk-shimmer-card" />)}
      </div>
    );
  }

  /* ---- REPORT VIEW ---- */
  if (view === "report" && currentResult) {
    return (
      <div className="sk-wrapper" style={{ paddingBottom: 80 }}>
        <div className="sk-report">
          <div className="sk-report-header">
            <img src="https://appcdn.goqii.com/storeimg/21983_1778484667.png" alt="GOQii" className="sk-report-logo" />
          </div>
          <p className="sk-report-title">{t("skinAssessment.title")}</p>
          <hr className="sk-report-divider" />

          <p className="sk-report-section-title">{t("skinAssessment.personalDetails")}</p>
          <div className="sk-report-grid">
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.name")}:</span><span className="sk-report-val">{userDetails.name}</span></div>
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.age")}:</span><span className="sk-report-val">{userDetails.age}</span></div>
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.gender")}:</span><span className="sk-report-val">{userDetails.gender}</span></div>
          </div>

          <p className="sk-report-section-title">{t("skinAssessment.resultsTitle")}</p>
          <div className="sk-report-grid">
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.skinType")}:</span><span className="sk-report-val">{currentResult.skinType}</span></div>
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.skinAcne")}:</span><span className="sk-report-val">{currentResult.skinAcne}</span></div>
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.sebumPct")}:</span><span className="sk-report-val">{currentResult.sebumPercentage}</span></div>
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.skinHydration")}:</span><span className="sk-report-val">{currentResult.skinHydration}</span></div>
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.skinWrinkle")}:</span><span className="sk-report-val">{currentResult.skinWrinkle}</span></div>
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.skinAge")}:</span><span className="sk-report-val">{currentResult.skinAge}</span></div>
            <div className="sk-report-row"><span className="sk-report-key">{t("skinAssessment.skinResult")}:</span><span className="sk-report-val">{currentResult.result}</span></div>
          </div>

          <div className="sk-gradient-divider" />

          <p className="sk-report-disclaimer"><strong>Disclaimer:</strong> This skin analysis report is generated using AI-based assessment technology and is intended for informational purposes only. It does not constitute medical advice, diagnosis, or treatment. Results may vary based on lighting conditions, camera quality, and image clarity. For a comprehensive evaluation of your skin or any underlying conditions, please consult a certified dermatologist or healthcare professional.</p>

          <div className="sk-report-actions">
            <button className="sk-btn-outline" onClick={() => setView("dashboard")}>{t("skinAssessment.close")}</button>
            <button className="sk-btn-primary" style={{ width: "auto" }} onClick={() => downloadPdf(currentResult.reportUrl)}>{t("skinAssessment.downloadPdf")}</button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- DASHBOARD VIEW ---- */
  return (
    <div className="sk-wrapper">
      <div className="sk-hero">
        <img src="https://appcdn.goqii.com/storeimg/92600_1778484765.png" alt="Skinalyze" className="sk-hero-img" />
      </div>
      <p className="sk-desc">{t("skinAssessment.desc")}</p>
      <button className="sk-btn-primary" onClick={() => window.open("https://goqii.com/app?fsn=246&fssn=0")}>{t("skinAssessment.startBtn")}</button>

      <hr className="sk-divider" />

      <p className="sk-section-title">{t("skinAssessment.previousAssessment")}</p>

      {results.length === 0 ? (
        <p className="sk-empty-text">{t("skinAssessment.noAssessments")}</p>
      ) : (
        results.map((r, idx) => (
          <div key={idx} className="sk-card" onClick={() => { setCurrentResult(r); setView("report"); }}>
            <p className="sk-card-date">{r.createdTime}</p>
            <div className="sk-card-row">
              <span className="sk-card-label">{t("skinAssessment.skinType")}:</span>
              <span className="sk-card-value">{r.skinType}</span>
            </div>
            <div className="sk-card-row">
              <span className="sk-card-label">{t("skinAssessment.skinAge")}:</span>
              <span className="sk-card-value">{r.skinAge}</span>
            </div>
            <div className="sk-card-actions">
              <button
                className="sk-btn-outline"
                onClick={(e) => { e.stopPropagation(); setCurrentResult(r); setView("report"); }}
              >
                {t("skinAssessment.viewReport")}
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default SkinAssessment;
